import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Menu, X, Globe } from "lucide-react";
import { Logo } from "@/components/Logo";
import { useLang } from "@/i18n";
import { Button } from "@/components/ui/button";

const langs = [{ k: "fr", l: "FR" }, { k: "nl", l: "NL" }, { k: "en", l: "EN" }];

const scrollTo = (id) => {
  const el = document.getElementById(id);
  if (el) el.scrollIntoView({ behavior: "smooth" });
};

export const Navbar = () => {
  const { lang, setLang, t } = useLang();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 30);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const links = [
    ["services", t.nav.services], ["compare", t.nav.compare], ["faq", t.nav.faq],
    ["blog", "Blog"], ["booking", t.nav.booking], ["contact", t.nav.contact],
  ];

  return (
    <motion.header
      initial={{ y: -80 }} animate={{ y: 0 }} transition={{ duration: 0.5 }}
      className={`fixed top-0 left-0 right-0 z-50 transition-colors duration-300 ${scrolled ? "glass" : "bg-transparent"}`}
      data-testid="navbar"
    >
      <div className="max-w-7xl mx-auto px-5 sm:px-8 h-16 sm:h-20 flex items-center justify-between">
        <a href="#top" onClick={(e) => { e.preventDefault(); window.scrollTo({ top: 0, behavior: "smooth" }); }}>
          <Logo showEmblem={false} />
        </a>

        <nav className="hidden lg:flex items-center gap-7">
          {links.map(([id, label]) => (
            <button key={id} onClick={() => scrollTo(id)} data-testid={`nav-${id}`}
              className="text-sm text-muted-foreground hover:text-white transition-colors duration-200">
              {label}
            </button>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <div className="hidden sm:flex items-center gap-1 rounded-full border border-white/10 p-1" data-testid="lang-switcher">
            <Globe className="w-3.5 h-3.5 text-muted-foreground ml-1.5" strokeWidth={1.5} />
            {langs.map((l) => (
              <button key={l.k} onClick={() => setLang(l.k)} data-testid={`lang-${l.k}`}
                className={`text-xs px-2 py-1 rounded-full transition-colors ${lang === l.k ? "bg-accent text-white" : "text-muted-foreground hover:text-white"}`}>
                {l.l}
              </button>
            ))}
          </div>
          <Button onClick={() => scrollTo("quote")} data-testid="nav-quote-btn"
            className="hidden sm:inline-flex rounded-full bg-white text-black hover:bg-accent hover:text-white transition-colors font-medium">
            {t.nav.quote}
          </Button>
          <button className="lg:hidden text-white" onClick={() => setOpen(!open)} data-testid="mobile-menu-btn">
            {open ? <X /> : <Menu />}
          </button>
        </div>
      </div>

      {open && (
        <div className="lg:hidden glass border-t border-white/10 px-5 py-4 flex flex-col gap-3" data-testid="mobile-menu">
          {links.map(([id, label]) => (
            <button key={id} onClick={() => { scrollTo(id); setOpen(false); }}
              className="text-left text-muted-foreground hover:text-white py-1">{label}</button>
          ))}
          <div className="flex gap-2 pt-2">
            {langs.map((l) => (
              <button key={l.k} onClick={() => setLang(l.k)}
                className={`text-xs px-3 py-1.5 rounded-full ${lang === l.k ? "bg-accent text-white" : "border border-white/10 text-muted-foreground"}`}>{l.l}</button>
            ))}
          </div>
          <Button onClick={() => { scrollTo("quote"); setOpen(false); }} className="rounded-full bg-white text-black mt-2">{t.nav.quote}</Button>
        </div>
      )}
    </motion.header>
  );
};
